import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export default function ContactForm() {
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  // File constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20 MB
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/gif', 'image/webp'];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Validate number of files
    if (selectedFiles.length + files.length > MAX_FILES) {
      alert(`Du kan maksimalt uploade ${MAX_FILES} billeder/filer i alt.`);
      return;
    }

    const validFiles = [];
    let currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

    for (const file of files) {
      // Validate type
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP understøttes.`);
        continue;
      }

      // Validate individual size
      if (file.size > MAX_FILE_SIZE) {
        alert(`Filen "${file.name}" er for stor. Maksimal filstørrelse er 10 MB.`);
        continue;
      }

      // Validate total size
      if (currentTotalSize + file.size > MAX_TOTAL_SIZE) {
        alert(`Den samlede filstørrelse overstiger grænsen på 20 MB.`);
        break;
      }

      currentTotalSize += file.size;
      validFiles.push(file);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    
    // Reset file input value so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Clear any existing files in FormData and append our validated files
    formData.delete('billede');
    selectedFiles.forEach((file) => {
      formData.append('billede', file);
    });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: formData,
        // Do NOT set Content-Type header, let browser set multipart/form-data with boundary
      });

      const result = await response.json();

      if (response.ok && result.status === 'ok') {
        setStatus('success');
        setSelectedFiles([]);
        form.reset();
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage('Forbindelsesfejl. Tjek din internetforbindelse og prøv igen.');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center max-w-2xl mx-auto shadow-sm">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 mb-4">
          <CheckCircle2 className="h-10 w-10" />
        </div>
        <h3 className="text-2xl font-bold text-emerald-900 mb-2">Tak for din henvendelse!</h3>
        <p className="text-emerald-700 mb-6">
          Vi har modtaget dine oplysninger og vil gennemgå dit projekt hurtigst muligt. Du kan forvente at høre fra os inden for 1-2 hverdage med et uforpligtende tilbud.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="inline-flex items-center justify-center px-6 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-1 transition-all duration-300"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-6 sm:p-10 shadow-xl border border-stone-100 space-y-6 max-w-3xl mx-auto"
    >
      <div className="border-b border-stone-100 pb-4 mb-6">
        <h3 className="text-xl font-bold text-stone-900">Få et uforpligtende tilbud</h3>
        <p className="text-sm text-stone-500 mt-1">
          Udfyld formularen nedenfor, så vender vi tilbage med rådgivning og et prisoverslag.
        </p>
      </div>

      {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
      <div className="sr-only absolute -top-96 -left-96" aria-hidden="true">
        <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke denne)</label>
        <input
          id="website_url"
          type="text"
          name="website_url"
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      {/* Error message */}
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3 text-red-800">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-red-600" />
          <div>
            <p className="font-semibold">Der opstod en fejl</p>
            <p className="text-sm">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Form Fields Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {/* Navn */}
        <div>
          <label htmlFor="navn" className="block text-sm font-semibold text-stone-700 mb-1.5">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="navn"
            name="navn"
            required
            placeholder="Dit fulde navn"
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900"
          />
        </div>

        {/* Telefon */}
        <div>
          <label htmlFor="telefon" className="block text-sm font-semibold text-stone-700 mb-1.5">
            Telefon <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="telefon"
            name="telefon"
            required
            placeholder="Tlf. nummer"
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900"
          />
        </div>

        {/* E-mail */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-stone-700 mb-1.5">
            E-mail <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            placeholder="din@email.dk"
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900"
          />
        </div>

        {/* Adresse */}
        <div>
          <label htmlFor="adresse" className="block text-sm font-semibold text-stone-700 mb-1.5">
            Adresse <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="adresse"
            name="adresse"
            required
            placeholder="Vejnavn, postnr. og by"
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900"
          />
        </div>

        {/* Hvad ønsker du lavet? */}
        <div>
          <label htmlFor="type" className="block text-sm font-semibold text-stone-700 mb-1.5">
            Hvad ønsker du lavet? <span className="text-red-500">*</span>
          </label>
          <select
            id="type"
            name="type"
            required
            defaultValue=""
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900 bg-white"
          >
            <option value="" disabled>Vælg belægningstype</option>
            <option value="Indkørsel">Indkørsel</option>
            <option value="Terrasse">Terrasse</option>
            <option value="Trappe">Trappe</option>
            <option value="Gangsti">Gangsti</option>
            <option value="Poolområde">Poolområde / Vådone</option>
            <option value="Andet">Andet (angiv i besked)</option>
          </select>
        </div>

        {/* Ca. antal m² */}
        <div>
          <label htmlFor="areal" className="block text-sm font-semibold text-stone-700 mb-1.5">
            Ca. antal m² <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="areal"
            name="areal"
            required
            min="1"
            placeholder="F.eks. 45"
            className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900"
          />
        </div>
      </div>

      {/* Besked */}
      <div>
        <label htmlFor="besked" className="block text-sm font-semibold text-stone-700 mb-1.5">
          Besked
        </label>
        <textarea
          id="besked"
          name="besked"
          rows="4"
          placeholder="Beskriv dit projekt, f.eks. nuværende underlag, farveønsker eller særlige forhold..."
          className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900 resize-y"
        ></textarea>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-semibold text-stone-700 mb-1.5">
          Upload billede(r) af det nuværende areal (valgfrit)
        </label>
        <p className="text-xs text-stone-500 mb-3">
          Hjælp os med at vurdere opgaven. Du kan uploade op til {MAX_FILES} billeder (maks. 10 MB pr. fil, format: JPG, PNG, WEBP, PDF).
        </p>
        
        <div className="flex flex-col gap-4">
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-stone-200 hover:border-emerald-600 rounded-xl p-6 text-center cursor-pointer transition-colors bg-stone-50/50 hover:bg-emerald-50/10 flex flex-col items-center justify-center gap-2"
          >
            <Upload className="h-8 w-8 text-stone-400" />
            <span className="text-sm font-medium text-stone-700">Klik for at vælge eller træk filer hertil</span>
            <span className="text-xs text-stone-400">PDF, PNG, JPG, GIF, WEBP op til 10 MB</span>
            <input
              ref={fileInputRef}
              type="file"
              name="billede"
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="bg-stone-50 rounded-xl p-4 border border-stone-100 space-y-2">
              <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Valgte filer ({selectedFiles.length}):</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-stone-200 text-sm">
                    <span className="truncate font-medium text-stone-700 max-w-[180px]">{file.name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-stone-400">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="text-stone-400 hover:text-red-500 p-1 rounded-full hover:bg-stone-100 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Henvisningskode / rabatkode */}
      <div>
        <label htmlFor="rabatkode" className="block text-sm font-semibold text-stone-700 mb-1.5">
          Henvisningskode / rabatkode (valgfrit)
        </label>
        <input
          type="text"
          id="rabatkode"
          name="rabatkode"
          placeholder="F.eks. SOMMER2026"
          className="w-full px-4 py-2.5 rounded-lg border border-stone-200 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/20 outline-none transition-all text-stone-900 max-w-xs"
        />
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 rounded-xl text-base font-bold bg-stone-900 hover:bg-stone-800 text-white hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none gap-2"
        >
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Sender tilbudsanmodning...
            </>
          ) : (
            'Send tilbudsanmodning'
          )}
        </button>
      </div>
    </form>
  );
}
